import { AuthClientInstance, getAuthClient } from "@trumpf/ux.utils";
import { authClientName, EnvConfig } from "../env-config";
import { decodeJwt } from "../../../utils/jwt/decode-jwt";

export interface RequestOptions {
    method?: string;
    headers?: HeadersInit;
    body?: unknown;
    responseType?: "json" | "text" | "blob";
}

export interface TokenData {
    access_token: string;
    id_token: string;
}

export interface IdTokenData {
    sub: string;
    email: string;
    email_verified: boolean;
    name: string;
    picture: string;
    given_name: string;
    family_name: string;
    locale: string;
    custom_attributes: {
        attr_id: string;
    };
}

export interface ApiClientInterface {
    request: <T>(path: string, options?: RequestOptions) => Promise<T>;
    setTokenData: (tokenData: TokenData) => void;
}

export class SalesforceApiClient implements ApiClientInterface {
    protected config: EnvConfig;
    protected token: string | null = null;
    protected authClient: AuthClientInstance = getAuthClient(authClientName);
    protected idTokenData: IdTokenData | null = null;

    constructor(config: EnvConfig) {
        this.config = config;
        this.token = this.authClient.getAccessToken();
        if (this.token) {
            this.onAuth();
        } else {
            this.authClient.subscribe((token) => {
                this.token = token || null;
                this.onAuth();
            });
        }
    }

    getSalesforceApiConfig(): EnvConfig["salesforceApi"] {
        return this.config.salesforceApi;
    }

    /** this is used for now because the authclient does not support salesforce at the moment */
    setTokenData(tokenData: TokenData) {
        this.token = tokenData.access_token;
        this.idTokenData = decodeJwt<IdTokenData>(tokenData.id_token);
    }

    getIdTokenData(): IdTokenData | null {
        return this.idTokenData;
    }

    protected onAuth() {
        // placeholder for any action to be taken on authentication
    }

    async request<T>(path: string, options?: RequestOptions): Promise<T> {
        if (this.authClient === null) {
            return Promise.reject("AuthClient not initialized");
        }
        if (!this.token) {
            return Promise.reject("No token");
        }

        const { method = "POST", headers = {}, body } = options || {};

        const response = await fetch(`${path}`, {
            method,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.token}`,
                ...headers,
            },
            body: JSON.stringify({
                ...(body as object),
            }),
        });

        if (response.status === 200) {
            if (options?.responseType === "blob") {
                return response.blob() as Promise<T>;
            }
            return response.json();
        }
        return Promise.reject(`Error ${response.status}`);
    }

    protected async mockRequest<T>(mockResponse: T): Promise<T> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(mockResponse);
            }, 1000);
        });
    }
}
